/**
 * Google Places API Service
 * Provides venue search functionality using Google Places API
 */

class GooglePlacesService {
    constructor() {
        this.placesService = null;
        this.isInitialized = false;
    }

    /**
     * Initialize the Google Places service
     * Must be called after Google Maps API is loaded
     */
    initialize() {
        try {
            console.log('Initializing Google Places Service...');

            // Check if Google Maps API is available
            if (!window.google) {
                throw new Error('Google Maps API not loaded - window.google is not available');
            }

            if (!window.google.maps) {
                throw new Error('Google Maps API not loaded - window.google.maps is not available');
            }

            if (!window.google.maps.places) {
                throw new Error('Google Maps Places library not loaded - ensure Places library is included in the API request');
            }

            // Check if PlacesService is available
            if (!window.google.maps.places.PlacesService) {
                throw new Error('PlacesService not available - check API key and billing');
            }

            console.log('Google Maps API and Places library detected');

            // Create a hidden map element required for Places service
            const mapDiv = document.createElement('div');
            mapDiv.style.display = 'none';
            mapDiv.style.width = '1px';
            mapDiv.style.height = '1px';
            document.body.appendChild(mapDiv);

            console.log('Creating map instance for Places service...');
            const map = new window.google.maps.Map(mapDiv, {
                center: { lat: 52.5200, lng: 13.4050 }, // Default to Berlin
                zoom: 13
            });

            console.log('Creating PlacesService instance...');
            this.placesService = new window.google.maps.places.PlacesService(map);

            // Test the service by making a dummy request to verify API key is working
            this.testPlacesService();

            this.isInitialized = true;
            console.log('Google Places Service initialized successfully');
        } catch (error) {
            console.error('Failed to initialize Google Places Service:', error);
            this.isInitialized = false;
            this.placesService = null;
            throw error;
        }
    }

    /**
     * Test the Places service to verify API key and permissions
     * @private
     */
    testPlacesService() {
        if (!this.placesService) {
            throw new Error('PlacesService not created');
        }

        console.log('Testing Places API with a simple request...');

        // Make a simple request to test the API
        const testRequest = {
            query: 'restaurant',
            fields: ['place_id', 'name']
        };

        // This will throw an error immediately if there are API key issues
        // We don't need to wait for the callback, just the attempt to call it
        try {
            this.placesService.textSearch(testRequest, (results, status) => {
                console.log('Places API test response:', status);
                if (status === window.google.maps.places.PlacesServiceStatus.REQUEST_DENIED) {
                    console.error('Places API test failed: REQUEST_DENIED - Check API key and enable Places API');
                } else if (status === window.google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT) {
                    console.error('Places API test failed: OVER_QUERY_LIMIT - Check billing and quotas');
                } else {
                    console.log('Places API test successful');
                }
            });
        } catch (error) {
            console.error('Places API test failed with exception:', error);
            throw new Error('Places API test failed - likely API key or billing issue');
        }
    }

    /**
     * Search for venues using Google Places Text Search
     * @param {string} query - Search query (e.g., "Venue Name City Country")
     * @param {Object} options - Additional search options
     * @returns {Promise<Array>} Array of venue results
     */
    async searchVenues(query, options = {}) {
        return new Promise((resolve, reject) => {
            if (!this.isInitialized) {
                console.error('Google Places Service not initialized');
                reject(new Error('Google Places Service not initialized'));
                return;
            }

            if (!this.placesService) {
                console.error('Places service is null');
                reject(new Error('Places service is not available'));
                return;
            }

            if (!query || typeof query !== 'string' || query.trim().length === 0) {
                console.error('Invalid search query:', query);
                reject(new Error('Search query must be a non-empty string'));
                return;
            }

            // Validate Google Maps API is available
            if (!window.google || !window.google.maps || !window.google.maps.places) {
                console.error('Google Maps API or Places library not available');
                reject(new Error('Google Maps API or Places library not available'));
                return;
            }

            const request = {
                query: query.trim(),
                type: 'establishment',
                ...options
            };

            console.log('Google Places request:', request);

            try {
                this.placesService.textSearch(request, (results, status) => {
                    try {
                        console.log('Google Places response - Status:', status, 'Results:', results);

                        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                            console.log('Places API returned OK status, processing results...');

                            // Filter results to prioritize venues that look like music venues
                            console.log('Filtering venue results...');
                            const filteredResults = this.filterVenueResults(results);
                            console.log('Filtered results:', filteredResults);

                            resolve(filteredResults);
                        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                            console.log(`No results found for query: ${query}`);
                            resolve([]);
                        } else {
                            const errorMessage = this.getStatusErrorMessage(status);
                            console.error('Places service error:', status, errorMessage);
                            reject(new Error(`Places service error: ${errorMessage}`));
                        }
                    } catch (callbackError) {
                        console.error('Error in textSearch callback:', callbackError);
                        reject(callbackError);
                    }
                });
            } catch (error) {
                console.error('Error calling textSearch:', error);
                reject(error);
            }
        });
    }

    /**
     * Get human-readable error message for Places API status
     * @param {string} status - Places API status
     * @returns {string} Human-readable error message
     */
    getStatusErrorMessage(status) {
        const statusMap = {
            [window.google.maps.places.PlacesServiceStatus.INVALID_REQUEST]: 'Invalid request parameters',
            [window.google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT]: 'API quota exceeded',
            [window.google.maps.places.PlacesServiceStatus.REQUEST_DENIED]: 'API request denied - check API key and permissions',
            [window.google.maps.places.PlacesServiceStatus.UNKNOWN_ERROR]: 'Unknown server error - please try again',
            [window.google.maps.places.PlacesServiceStatus.NOT_FOUND]: 'No results found',
        };

        return statusMap[status] || `Unknown status: ${status}`;
    }

    /**
     * Filter and prioritize venue results
     * Prioritizes venues that are likely to be music venues or event spaces
     * @param {Array} results - Raw Google Places results
     * @returns {Array} Filtered and prioritized results
     */
    filterVenueResults(results) {
        try {
            console.log('filterVenueResults called with:', results);

            if (!results || !Array.isArray(results) || results.length === 0) {
                console.log('No results to filter');
                return [];
            }

            console.log(`Processing ${results.length} venue results...`);

            // Define venue type priorities for music venues
            const venueTypeScore = (types) => {
                try {
                    if (!types || !Array.isArray(types)) return 0;

                    const scores = {
                        'night_club': 10,
                        'establishment': 8,
                        'point_of_interest': 7,
                        'tourist_attraction': 6,
                        'amusement_park': 5,
                        'stadium': 9,
                        'gym': 4,
                        'restaurant': 3,
                        'bar': 6,
                        'lodging': 2,
                        'store': 1
                    };

                    let maxScore = 0;
                    types.forEach(type => {
                        if (scores[type] && scores[type] > maxScore) {
                            maxScore = scores[type];
                        }
                    });
                    return maxScore;
                } catch (error) {
                    console.error('Error in venueTypeScore:', error);
                    return 0;
                }
            };

            // Score and sort results
            console.log('Scoring results...');
            const scoredResults = results.map((result, index) => {
                try {
                    console.log(`Processing result ${index}:`, result);
                    return {
                        ...result,
                        venueScore: venueTypeScore(result.types || [])
                    };
                } catch (error) {
                    console.error(`Error processing result ${index}:`, error);
                    return { ...result, venueScore: 0 };
                }
            });

            console.log('Sorting results...');
            // Sort by venue score (descending) and then by rating
            scoredResults.sort((a, b) => {
                try {
                    if (a.venueScore !== b.venueScore) {
                        return b.venueScore - a.venueScore;
                    }
                    return (b.rating || 0) - (a.rating || 0);
                } catch (error) {
                    console.error('Error sorting results:', error);
                    return 0;
                }
            });

            console.log('Transforming final results...');
            // Return top 5 results
            const finalResults = scoredResults.slice(0, 5).map((result, index) => {
                try {
                    console.log(`Transforming result ${index}:`, result);

                    // Safely extract location coordinates
                    let lat, lng;
                    if (result.geometry && result.geometry.location) {
                        try {
                            if (typeof result.geometry.location.lat === 'function') {
                                // Google Maps LatLng object
                                lat = result.geometry.location.lat();
                                lng = result.geometry.location.lng();
                            } else {
                                // Plain object with lat/lng properties
                                lat = result.geometry.location.lat;
                                lng = result.geometry.location.lng;
                            }
                        } catch (locationError) {
                            console.error('Error extracting location coordinates:', locationError);
                            lat = null;
                            lng = null;
                        }
                    }

                    return {
                        place_id: result.place_id,
                        name: result.name,
                        formatted_address: result.formatted_address,
                        geometry: {
                            location: {
                                lat: lat,
                                lng: lng
                            }
                        },
                        rating: result.rating,
                        user_ratings_total: result.user_ratings_total,
                        types: result.types,
                        photos: result.photos ? result.photos.slice(0, 1) : [], // First photo only
                        opening_hours: result.opening_hours,
                        website: result.website,
                        formatted_phone_number: result.formatted_phone_number,
                        vicinity: result.vicinity,
                        venueScore: result.venueScore
                    };
                } catch (transformError) {
                    console.error(`Error transforming result ${index}:`, transformError);
                    return {
                        place_id: result.place_id || `error-${index}`,
                        name: result.name || 'Error loading venue',
                        formatted_address: result.formatted_address || '',
                        geometry: { location: { lat: null, lng: null } },
                        rating: result.rating,
                        user_ratings_total: result.user_ratings_total,
                        types: result.types || [],
                        photos: [],
                        opening_hours: null,
                        website: result.website,
                        formatted_phone_number: result.formatted_phone_number,
                        vicinity: result.vicinity,
                        venueScore: 0
                    };
                }
            });

            console.log('Filter complete, returning results:', finalResults);
            return finalResults;

        } catch (error) {
            console.error('Error in filterVenueResults:', error);
            return [];
        }
    }

    /**
     * Get detailed information about a specific place
     * @param {string} placeId - Google Places ID
     * @returns {Promise<Object>} Detailed place information
     */
    async getPlaceDetails(placeId) {
        return new Promise((resolve, reject) => {
            if (!this.isInitialized) {
                reject(new Error('Google Places Service not initialized'));
                return;
            }

            const request = {
                placeId: placeId,
                fields: [
                    'place_id',
                    'name',
                    'formatted_address',
                    'geometry',
                    'rating',
                    'user_ratings_total',
                    'types',
                    'photos',
                    'opening_hours',
                    'website',
                    'formatted_phone_number',
                    'international_phone_number',
                    'vicinity',
                    'url',
                    'reviews'
                ]
            };

            this.placesService.getDetails(request, (place, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    // Safely extract location coordinates
                    let lat, lng;
                    if (place.geometry && place.geometry.location) {
                        if (typeof place.geometry.location.lat === 'function') {
                            // Google Maps LatLng object
                            lat = place.geometry.location.lat();
                            lng = place.geometry.location.lng();
                        } else {
                            // Plain object with lat/lng properties
                            lat = place.geometry.location.lat;
                            lng = place.geometry.location.lng;
                        }
                    }

                    resolve({
                        place_id: place.place_id,
                        name: place.name,
                        formatted_address: place.formatted_address,
                        geometry: {
                            location: {
                                lat: lat,
                                lng: lng
                            }
                        },
                        rating: place.rating,
                        user_ratings_total: place.user_ratings_total,
                        types: place.types,
                        photos: place.photos,
                        opening_hours: place.opening_hours,
                        website: place.website,
                        formatted_phone_number: place.formatted_phone_number,
                        international_phone_number: place.international_phone_number,
                        vicinity: place.vicinity,
                        url: place.url,
                        reviews: place.reviews
                    });
                } else {
                    reject(new Error(`Place details error: ${status}`));
                }
            });
        });
    }

    /**
     * Search for venues with geographic bias
     * @param {string} query - Search query
     * @param {Object} location - {lat, lng} to bias search results
     * @param {number} radius - Search radius in meters (optional)
     * @returns {Promise<Array>} Array of venue results
     */
    async searchVenuesNear(query, location, radius = 50000) {
        const options = {
            location: new window.google.maps.LatLng(location.lat, location.lng),
            radius: radius
        };

        return this.searchVenues(query, options);
    }

    /**
     * Check if the service is ready to use
     * @returns {boolean} True if service is initialized
     */
    isReady() {
        return this.isInitialized && this.placesService !== null;
    }
}

// Create singleton instance
const googlePlacesService = new GooglePlacesService();

export default googlePlacesService;